import React, { useState } from 'react';
import './CarePlusNotificationBanner.css';
import * as translator from '../../../../../utils/translator';
import { toLocaleDateString } from '../../../../../utils/helpers';
import * as http from '../../../../../utils/http';
import { NOTIFICATION_VARIANT } from '../../CarePlusOrderDetail/carePlusConstants';
import CapsuleIcon from '../../../../../icons/careplus-notif-capsule.svg';
import PausedIcon from '../../../../../icons/careplus-notif-pause.svg';
import CardIcon from '../../../../../icons/notification-card.svg';
import CalendarIcon from '../../../../../icons/notification-calendar.svg';
import BoxIcon from '../../../../../icons/usp-box.svg';
import VideoIcon from '../../../../../icons/video-camera.svg';

const NOTIFICATION_ICON: Record<CarePlusNotification['type'], string> = {
  dose_adjustment_pending: CapsuleIcon,
  payment_failed: CardIcon,
  paused: PausedIcon,
  renewal_upcoming: CalendarIcon,
  out_of_stock: BoxIcon,
  video_consultations: VideoIcon,
};

interface CarePlusNotificationBannerProps {
  notification: CarePlusNotification;
  subscriptionID: number;
  subscriptionHashID: string;
  language: string;
  locale: string;
  setRefreshData: (updater: (prev: boolean) => boolean) => void;
  setStickyNotificationDetails: (details: any) => void;
  onFocusPaymentMethod?: () => void;
}

const CarePlusNotificationBanner: React.FC<CarePlusNotificationBannerProps> = ({
  notification,
  subscriptionID,
  subscriptionHashID,
  language,
  locale,
  setRefreshData,
  setStickyNotificationDetails,
  onFocusPaymentMethod,
}) => {
  const [loading, setLoading] = useState(false);
  const variant = NOTIFICATION_VARIANT[notification.type];
  const { data } = notification;

  const t = (path: string) => translator.translate(language, 'my_account', path);

  const doPost = (endpoint: string, body: Record<string, unknown>, successTitleKey: string, successBodyKey: string) => {
    setLoading(true);
    return http.postToServer(endpoint, body, [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER])
      .then(() => {
        setRefreshData((p) => !p);
        setStickyNotificationDetails({
          show: true,
          level: 'success',
          title: t(successTitleKey),
          content: t(successBodyKey),
        });
      })
      .catch(() => setStickyNotificationDetails({
        show: true,
        level: 'error',
        title: t('CarePlus/notifications/errorTitle'),
        content: t('CarePlus/notifications/errorBody'),
      }))
      .finally(() => setLoading(false));
  };

  const renderContent = () => {
    switch (notification.type) {
      case 'dose_adjustment_pending': {
        const body = (t('CarePlus/notifications/doseAdjustment/body') || '')
          .replace(':old', data.oldPrice != null ? translator.getPrice(data.oldPrice, locale) : '')
          .replace(':new', data.newPrice != null ? translator.getPrice(data.newPrice, locale) : '');
        return (
          <>
            <p className="careplus-banner__title" dangerouslySetInnerHTML={{ __html: t('CarePlus/notifications/doseAdjustment/title') }} />
            {body && <p className="careplus-banner__body">{body}</p>}
            <div className="careplus-banner__actions">
              <button
                type="button"
                className="careplus-banner__btn careplus-banner__btn--green"
                disabled={loading}
                onClick={() => doPost('account-update/subscription/care-plus/price-change/approve', { subscriptionID }, 'CarePlus/notifications/approveSuccessTitle', 'CarePlus/notifications/approveSuccessBody')}
              >
                {t('CarePlus/notifications/doseAdjustment/approve')}
              </button>
              <button
                type="button"
                className="careplus-banner__btn careplus-banner__btn--link"
                disabled={loading}
                onClick={() => doPost('account-update/subscription/care-plus/price-change/decline', { subscriptionID }, 'CarePlus/notifications/declineSuccessTitle', 'CarePlus/notifications/declineSuccessBody')}
              >
                {t('CarePlus/notifications/doseAdjustment/decline')}
              </button>
            </div>
          </>
        );
      }
      case 'payment_failed': {
        // Paused after the grace period: no deadline left, a new payment method resumes the plan.
        const template = data.paused
          ? t('CarePlus/notifications/paymentFailed/bodyPaused')
          : data.last4
            ? t('CarePlus/notifications/paymentFailed/body')
            : t('CarePlus/notifications/paymentFailed/bodyNoCard');
        const dateOptions = { year: 'numeric' as const, month: 'long' as const, day: 'numeric' as const };
        const body = (template || '')
          .replace(':last4', data.last4 ?? '')
          .replace(':date', data.deadline ? toLocaleDateString(data.deadline, language, dateOptions) : '');
        return (
          <>
            <p className="careplus-banner__title" dangerouslySetInnerHTML={{ __html: t('CarePlus/notifications/paymentFailed/title') }} />
            {body && <p className="careplus-banner__body" dangerouslySetInnerHTML={{ __html: body }} />}
            <div className="careplus-banner__actions">
              <button
                type="button"
                className="careplus-banner__btn careplus-banner__btn--black"
                onClick={() => onFocusPaymentMethod?.()}
              >
                {t('CarePlus/notifications/paymentFailed/cta')}
              </button>
            </div>
          </>
        );
      }
      case 'paused': {
        const dateOptions = { year: 'numeric' as const, month: 'long' as const, day: 'numeric' as const };
        return (
          <>
            <p className="careplus-banner__title" dangerouslySetInnerHTML={{ __html: t('CarePlus/notifications/paused/title') }} />
            {data.resumeAt && <p className="careplus-banner__body">{(t('CarePlus/notifications/paused/body') || '').replace(':date', toLocaleDateString(data.resumeAt, language, dateOptions))}</p>}
            <p className="careplus-banner__body">{t('CarePlus/pauseModal/confirmPauseInfo')}</p>
            {/* Only a pause the customer set themselves can be ended here; system and support pauses are ended by support. */}
            {data.canResume !== false && (
              <div className="careplus-banner__actions">
                <button
                  type="button"
                  className="careplus-banner__btn careplus-banner__btn--black"
                  disabled={loading}
                  onClick={() => doPost('account-update/subscription/care-plus/resume', { subscriptionID }, 'CarePlus/notifications/resumeSuccessTitle', 'CarePlus/notifications/resumeSuccessBody')}
                >
                  {t('CarePlus/notifications/paused/cta')}
                </button>
              </div>
            )}
          </>
        );
      }
      case 'renewal_upcoming': {
        const dateOptions = { year: 'numeric' as const, month: 'long' as const, day: 'numeric' as const };
        const formattedRenewalDate = data.renewalDate
          ? toLocaleDateString(data.renewalDate, language, dateOptions)
          : '';
        // No cycles = one-cycle plan (Basic / Monthly): no ":period-month" or "N ×" breakdown.
        const copyKey = data.cycles ? 'CarePlus/notifications/renewal' : 'CarePlus/notifications/renewalMonthly';
        const title = data.renewalDate
          ? (t(`${copyKey}/title`) || '').replace(':date', formattedRenewalDate).replace(':period', String(data.cycles ?? ''))
          : t(`${copyKey}/title`);
        const body = (t(`${copyKey}/body`) || '')
          .replace(':total', data.total != null ? translator.getPrice(data.total, locale) : '')
          .replace(':cycles', String(data.cycles ?? ''))
          .replace(':amount', data.amount != null ? translator.getPrice(data.amount, locale) : '');
        return (
          <>
            <p className="careplus-banner__title" dangerouslySetInnerHTML={{ __html: title }} />
            {body && <p className="careplus-banner__body" dangerouslySetInnerHTML={{ __html: body }} />}
          </>
        );
      }
      case 'out_of_stock': {
        return (
          <>
            <p className="careplus-banner__title" dangerouslySetInnerHTML={{ __html: t('CarePlus/notifications/outOfStock/title') }} />
            <p className="careplus-banner__body">{t('CarePlus/notifications/outOfStock/body')}</p>
            <div className="careplus-banner__actions">
              <button
                type="button"
                className="careplus-banner__btn careplus-banner__btn--orange"
                disabled={loading}
                onClick={() => doPost('account-update/subscription/care-plus/wait-for-stock', { subscriptionHashId: subscriptionHashID }, 'CarePlus/notifications/waitSuccessTitle', 'CarePlus/notifications/waitSuccessBody')}
              >
                {t('CarePlus/notifications/outOfStock/wait')}
              </button>
              <button
                type="button"
                className="careplus-banner__btn careplus-banner__btn--link"
                disabled={loading}
                onClick={() => doPost('account-update/subscription/care-plus/cancel-out-of-stock', { subscriptionHashId: subscriptionHashID }, 'CarePlus/notifications/cancelOrderSuccessTitle', 'CarePlus/notifications/cancelOrderSuccessBody')}
              >
                {t('CarePlus/notifications/outOfStock/cancel')}
              </button>
            </div>
          </>
        );
      }
      case 'video_consultations': {
        return (
          <>
            <p className="careplus-banner__title" dangerouslySetInnerHTML={{ __html: t('CarePlus/notifications/video/title') }} />
            <p className="careplus-banner__body">{t('CarePlus/notifications/video/body')}</p>
            <div className="careplus-banner__actions">
              <button
                type="button"
                className="careplus-banner__btn careplus-banner__btn--green"
                disabled={loading}
                onClick={() => doPost('account-update/subscription/care-plus/schedule-video', { subscriptionHashId: subscriptionHashID }, 'CarePlus/notifications/videoSuccessTitle', 'CarePlus/notifications/videoSuccessBody')}
              >
                {t('CarePlus/notifications/video/cta')}
              </button>
            </div>
          </>
        );
      }
      default:
        return null;
    }
  };

  const content = renderContent();
  if (!content) return null;

  const iconCircleClass = notification.type === 'paused'
    ? `careplus-banner__icon-circle careplus-banner__icon-circle--${variant} careplus-banner__icon-circle--paused`
    : `careplus-banner__icon-circle careplus-banner__icon-circle--${variant}`;

  return (
    <div className={`careplus-banner careplus-banner--${variant}`}>
      <div className={iconCircleClass} aria-hidden="true">
        <img className="careplus-banner__icon" src={NOTIFICATION_ICON[notification.type]} alt="" />
      </div>
      <div className="careplus-banner__content">{content}</div>
    </div>
  );
};

export default CarePlusNotificationBanner;
